import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";

import {
  fetchAssystProviderList,
  fetchAssystDataProvider,
  updateBulkValuation,
  clearError,
  fetchProviderById,
} from "../../../redux/SystemManager/bulkValuationSlice";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  Field,
  Option,
  Dropdown,
  Toaster,
  Label,
} from "@fluentui/react-components";
import ASCButton from "../../../components/Button";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import { useId } from "@fluentui/react-components";
import OverlayLoader from "../../../components/OverlayLoader";

const BulkValuation = ({ selectedProvider, handleCancel }: any) => {
  const { showToast, toasterId } = useToastMessage();
  const providerDropdownId = useId("providerDropdown");

  const dataProviderDropdownId = useId("dataProviderDropdown");
  const [apiProcessing, setApiProcessing] = useState(false);
  const dispatch: AppDispatch = useDispatch();
  const { selectedProviderData, providers, dataProvider, loading, error } =
    useSelector((state: RootState) => state.bulkValuation);
  // State for mode (View/Edit)
  const [mode, setMode] = useState<"view" | "edit">(() => {
    if (
      selectedProviderData?.assystInvestmentProviderId &&
      selectedProviderData?.assystDataProviderId
    ) {
      return "view";
    }
    return "edit";
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      investmentProviderId:
        selectedProviderData?.assystInvestmentProviderId || "",
      dataProviderId: selectedProviderData?.assystDataProviderId || "",
    },
    validate: (values: any) => {
      const errors: any = {};
      if (!values.investmentProviderId) {
        errors.investmentProviderId = "Assyst Provider is required";
      }
      if (values.investmentProviderId && !values.dataProviderId) {
        errors.dataProviderId = "Please select a valid Data Provider";
      }
      return errors;
    },
    onSubmit: (values: any) => {
      handleUpdate(values);
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (selectedProvider?.id) {
      dispatch(fetchProviderById(selectedProvider.id));
    }
  }, [dispatch, selectedProvider?.id]);

  useEffect(() => {
    dispatch(fetchAssystProviderList());
  }, [dispatch]);

  // Update mode when selectedProviderData changes
  useEffect(() => {
    setMode(
      selectedProviderData?.assystInvestmentProviderId &&
        selectedProviderData?.assystDataProviderId
        ? "view"
        : "edit"
    );
  }, [selectedProviderData]);

  // Map providers to dropdown options
  const providerOptions: any[] = providers
    ? providers.map((provider) => ({
        key: provider.InvestmentId,
        text: provider.Name,
      }))
    : [];

  // Map data provider to dropdown options
  const dataProviderOptions: any[] = dataProvider
    ? dataProvider.map((dataProvider) => ({
        key: dataProvider.DataProviderId,
        text: dataProvider.Name,
      }))
    : [];

  // Handle update submission
  const handleUpdate = async (values: any) => {
    setApiProcessing(true);
    try {
      const updateData = {
        providerId: selectedProvider.id,
        investmentProviderId: values.investmentProviderId,
        dataProviderId: values.dataProviderId,
      };
      const result = await dispatch(
        updateBulkValuation({ data: updateData })
      ).unwrap();

      if (!result.isError) {
        setMode("view");
        dispatch(fetchProviderById(selectedProvider.id));
        showToast(
          result.isError ? result.error.message : result.message,
          "",
          result.isError ? "error" : "success"
        );
      }
    } catch (err: any) {
      showToast("Failed to update bulk valuation", "", "error");
    } finally {
      setApiProcessing(false);
    }
  };

  // Handle edit button click
  const handleEditClick = () => {
    setMode("edit");
    formik.resetForm({
      values: {
        investmentProviderId:
          selectedProviderData?.AssystInvestmentProviderId || "",
        dataProviderId: selectedProviderData?.AssystDataProviderId || "",
      },
    });
  };
  useEffect(() => {
    if (formik?.values?.investmentProviderId) {
      dispatch(fetchAssystDataProvider(formik.values?.investmentProviderId));
      formik.setFieldValue("dataProviderId", dataProviderOptions[0]?.key);
    } else {
      dispatch(clearError());
    }
  }, [formik.values?.investmentProviderId, dispatch, dataProvider]);

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      {loading && <OverlayLoader isLoading={loading} message="Loading..." />}
      <>
        {mode === "view" && (
          <div className="flex-end">
            <ASCButton
              onClick={handleEditClick}
              appearance="outline"
              label="Edit"
              shape="rounded"
            />
          </div>
        )}

        {mode === "view" ? (
          <div
            className="admin_container_flex"
            style={{
              backgroundColor: "#f9f9f9",
              padding: 10,
              overflowY: "visible",
              margin: "10px 0",
              height: "fit-content",
            }}
          >
            <div className="grid-container">
              <div className="page-top-area">
                <Label weight="semibold" size="medium">
                  Assyst Provider
                </Label>
                <Label>{selectedProviderData?.assystInvestmentProvider}</Label>
              </div>
              <div className="page-top-area">
                <Label weight="semibold" size="medium">
                  Assyst Data Provider
                </Label>
                <Label>{selectedProviderData?.assystDataProvider}</Label>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div
              className="admin_container_flex"
              style={{
                backgroundColor: "#f9f9f9",
                padding: 10,
                overflowY: "visible",
                margin: "10px 0",
                height: "fit-content",
              }}
            >
              <div>
                <div className="grid-container">
                  <Field
                    label={
                      <span>
                        Select Assyst Provider
                        <span className="required-item">*</span>
                      </span>
                    }
                    validationState={
                      formik.errors.investmentProviderId ? "error" : "none"
                    }
                    validationMessage={
                      formik.errors.investmentProviderId as string
                    }
                  >
                    <Dropdown
                      onFocus={() =>
                        formik.setFieldTouched("investmentProviderId")
                      }
                      id={providerDropdownId}
                      placeholder="Select Assyst Provider"
                      value={
                        providerOptions.find(
                          (opt) =>
                            opt.key === formik.values.investmentProviderId
                        )?.text ?? ""
                      }
                      onOptionSelect={(e, data) => {
                        formik.setFieldValue(
                          "investmentProviderId",
                          data.optionValue
                        );
                      }}
                    >
                      {providerOptions.map((option) => (
                        <Option key={option.key} value={option.key}>
                          {option.text}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>

                  <Field
                    label={
                      <span>
                        Select Assyst Data Provider
                        <span className="required-item">*</span>
                      </span>
                    }
                    validationState={
                      formik.errors.dataProviderId ? "error" : "none"
                    }
                    validationMessage={formik.errors.dataProviderId as string}
                  >
                    <Dropdown
                      id={dataProviderDropdownId}
                      placeholder="Select Assyst Data Provider"
                      value={
                        dataProviderOptions.find(
                          (opt) => opt.key === formik.values.dataProviderId
                        )?.text ?? ""
                      }
                      disabled={!formik.values.investmentProviderId}
                      onOptionSelect={(e, data) => {
                        formik.setFieldValue(
                          "dataProviderId",
                          data.optionValue
                        );
                      }}
                    >
                      {dataProviderOptions.length === 0 && (
                        <Option value="" disabled={true}>
                          No Data Provider found for the selected Case Provider
                        </Option>
                      )}
                      {dataProviderOptions.map((option) => (
                        <Option key={option.key} value={option.key}>
                          {option.text}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                </div>
              </div>
            </div>
            <div className="load-action-container" style={{ margin: "10px 0" }}>
              <ASCButton
                onClick={() => formik.submitForm()}
                disabled={
                  apiProcessing || Object.keys(formik.errors).length !== 0
                }
                appearance="primary"
                className="asc-button-primary"
                label="Save"
                shape="rounded"
              />
              <ASCButton
                appearance="outline"
                shape="rounded"
                label="Cancel"
                onClick={() => {
                  formik.resetForm();
                  if (
                    selectedProviderData?.assystInvestmentProviderId &&
                    selectedProviderData?.assystDataProviderId
                  ) {
                    setMode("view");
                  } else {
                    handleCancel();
                  }
                }}
              />
            </div>
          </div>
        )}
      </>
    </>
  );
};

export default BulkValuation;
